import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { PlanPurchase } from "../models/plan-purchase.model";
import { ChildOrgActivePlans } from "../interface/child-org-active-plans.interface";
import { Order } from "../../order/interface/order.interface";
import { PlanType } from "../../plan/lib/enum/plan-type.enum";
import { firstValueFrom } from "rxjs";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { RestService } from "../../../services/rest.service";
import { RestUtil } from "../../../services/rest.util";
import { PaginatedResponse } from "../../../interface/PaginatedResponse";
import { AuthSelectors } from "../../authentication";
import { UsersSelectors } from "../../user";
import { Browser } from "@capacitor/browser";

@Injectable({
  providedIn: 'root'
})
export class PlanPurchaseService {

  public readonly apiEndpoint = 'plan-purchase'

  constructor(private restService: RestService,
    private http: HttpClient,
    private store: Store,
    private router: Router
  ) {
  }


  async readAll() {

    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId))

    if (!account_id) {
      return
    }

    return (await this.restService.send<PlanPurchase[]>({
      url: this.apiEndpoint,
      method: 'GET',
      account_id: account_id,
      query: {
        orgId: account_id,
      }

    })).data;
  }

  /**
   * True when the org previously purchased a primary plan but none is currently active —
   * i.e. the subscription has expired (distinct from a free-tier org that never purchased).
   */
  async isPrimaryPlanExpired(): Promise<boolean> {
    const [activePlans, allPurchases] = await Promise.all([
      this.readActivePlans(),
      this.readAll(),
    ]);

    const hasActivePrimary = (activePlans || []).some(p => p.plan?.type === PlanType.primary);
    const hadPrimary = (allPurchases || []).some(p => p.plan?.type === PlanType.primary);

    return hadPrimary && !hasActivePrimary;
  }

  /** Active (non-expired) plans for the org — drives the top "Plan" section. */
  async readActivePlans(): Promise<PlanPurchase[]> {

    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId))

    if (!account_id) {
      return [];
    }

    return (await this.restService.send<PlanPurchase[]>({
      url: this.apiEndpoint + '/active',
      method: 'GET',
      account_id: account_id,
      query: {
        orgId: account_id,
      }
    })).data || [];
  }

  /** Active plans for every child org of the current (parent) account. */
  async readActivePlansForChildren(): Promise<ChildOrgActivePlans[]> {
    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));

    if (!account_id) {
      return [];
    }

    return (await this.restService.send<ChildOrgActivePlans[]>({
      url: this.apiEndpoint + '/active/children',
      method: 'GET',
      account_id: account_id,
    })).data || [];
  }

  /**
   * Paginated billing history (lazy loading / infinite scroll).
   * Sourced from the Orders collection (completed orders), with invoice data + resolved plan per row.
   */
  async readBillingHistory(page: number = 1): Promise<PaginatedResponse<Order>> {

    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId))

    if (!account_id) {
      return { docs: [] };
    }

    return (await this.restService.send<PaginatedResponse<Order>>({
      url: 'order',
      method: 'GET',
      account_id: account_id,
      query: {
        page: page,
        paginated: true,
      }
    })).data || { docs: [] };
  }

  /** Immediately removes an active plan purchase for the current org. */
  async removePlan(planPurchaseId: string, remark = ''): Promise<PlanPurchase | null> {
    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));

    if (!account_id) {
      return null;
    }

    return (await this.restService.send<PlanPurchase>({
      url: this.apiEndpoint + '/remove/' + planPurchaseId,
      method: 'PUT',
      account_id,
      body: { remark },
    })).data;
  }

  /** Downloads the invoice PDF for an order and triggers a browser save. */
  async downloadInvoice(orderId: string, invoiceNumber?: string): Promise<void> {

    let headers = new HttpHeaders()
      .set('Authorization', 'Bearer ' + RestUtil.AuthToken);

    if (RestUtil.Account_id) {
      headers = headers.set('account_id', RestUtil.Account_id);
    }
    if (RestUtil.AccountType) {
      headers = headers.set('accountType', RestUtil.AccountType);
    }

    const blob = await firstValueFrom(this.http.get(
      `${RestUtil.BaseUrl}order/${orderId}/invoice`,
      { headers, responseType: 'blob' }
    ));

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${invoiceNumber || 'invoice'}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  // TODO: Remove this once the upgrade plan page is implemented
  // async openPlanUpgrade() {
  //   // Navigate to the upgrade plan page within the app
  //   await this.router.navigate(['/upgrade-plan']);
  // }

  async openPlanUpgrade() {
    const org = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccount))
    const authenticatedUser = await firstValueFrom(this.store.select(UsersSelectors.selectAuthenticatedUser))
    let userName = encodeURIComponent(authenticatedUser.name);
    let userEmail = encodeURIComponent(authenticatedUser.email);
    let orgName = encodeURIComponent(org.name);
    let orgPhone = encodeURIComponent(authenticatedUser.phoneNumber)
    await Browser.open({
      url: `https://oneqid.com/upgrade/?user_name=${userName}&user_email=${userEmail}&org_name=${orgName}&org_phone=${orgPhone}`,
      toolbarColor: '#f1f5f5',
      presentationStyle: 'popover',
    });
  }
}
